import { Fragment, useEffect, useState } from 'react'
import useMergedData from '../hooks/useMergedData'
import useComputeNodes from '../hooks/useComputeNodes'
import useNode, { isNodeOffline } from '../models/useNode'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import NodeMarks from './NodeMarks'
import NodeVersionMark from './NodeVersionMark'
import IconNode from './IconNode'
import ListSubheader from '@mui/material/ListSubheader'
import Box from '@mui/material/Box'
import ListItemLink from './ListItemLink'
import ScrollList from './ScrollList'
import NodesEmptyState from './documentation/NodesEmptyState'
import AddThisPhone from './AddThisPhone'
import { usePhoneNodeId } from '../models/usePhoneNode'
import { countByNetwork, countLabel, offlineLabel } from '../utilities/nodeStatus'
import useOutsideNodes from '../hooks/useOutsideNodes'
import { nodePagePath, nodeRows } from '../utilities/nodeGroups'

// "Offline for 3 h" is computed at render. A store change does not come for every minute that
// passes, so the list renders again once a minute to keep the age current.
const TICK_MS = 60_000

/** The height of one row, in px, as the harness measures it (`?panel=lists&multi=1`): 60.8. */
const ROW_HEIGHT = 61
/** A network's list shrinks to share the drawer, and not below 3½ rows. The half row says there is more. */
const MIN_ROWS = 3.5

/**
 * `+1` on a row of a node in several places, and its tooltip names the others (app#498). In a list
 * of sections, this mark is the only sign that the row appears elsewhere too. Nothing for a node
 * in one place.
 */
function AlsoInMark({ others }: Readonly<{ others: string[] }>) {
  if (!others.length) return null
  return (
    <Tooltip title={`Also in ${others.join(', ')}`}>
      <Typography variant="caption" color="textSecondary" component="span">
        +{others.length}
      </Typography>
    </Tooltip>
  )
}

export default function Nodes() {
  const merged = useMergedData()
  // Inside the noBGP app, this phone is the list's first row (AddThisPhone), not a row of its group.
  const phoneId = usePhoneNodeId()
  const { networks } = merged
  const nodes = phoneId ? merged.nodes.filter(n => n.id !== phoneId) : merged.nodes
  // The active org's nodes that no network group holds (app#388): in another org's network, or in
  // no network. `useMergedData` drops them, because their network is not loaded.
  const outside = useOutsideNodes().filter(o => o.node.id !== phoneId)
  const compute = useComputeNodes()
  // ⚠ Completeness comes from the STORE, not from `hideOffline`: see `useNode.nodesComplete`.
  const nodesComplete = useNode(state => state.nodesComplete)
  const [now, setNow] = useState(() => Date.now())
  // ⚠ The clock runs only while a row's text can still change — the same rule
  // `useComputeNodes` states. Past a day `offlineLabel` returns a fixed date, and an online node
  // reads "Online", so a tick over such a fleet re-renders the whole drawer for nothing.
  const ticking = [...nodes, ...outside.map(o => o.node)].some(node => {
    if (!isNodeOffline(node) || !node.offlineAt) return false
    const t = new Date(node.offlineAt).getTime()
    return !Number.isNaN(t) && now - t < 24 * 3600_000
  })
  useEffect(() => {
    if (!ticking) return
    const id = setInterval(() => setNow(Date.now()), TICK_MS)
    return () => clearInterval(id)
  }, [ticking])
  // ⚠ Not memoized: `useMergedData` returns a fresh `nodes` array on every render.
  const counts = countByNetwork(nodes, isNodeOffline)

  const rows = nodeRows(nodes, networks, outside)
  // The count of an outside group: the same count as a network's, keyed by the group.
  const outsideCounts = countByNetwork(
    outside.map(({ node, group }) => ({ node, networkIds: [group] })),
    ({ node }) => isNodeOffline(node)
  )

  // ⚠ Group by network ID, never by name. `unique_network_name` is (owner_id, name), so one org can
  // hold two networks called `home` from two owners; grouping by name merges them under one
  // subheader whose count is taken from the first row's network only, and then states a total the
  // rows below it contradict. An outside group's key is its group name, which no network id can
  // equal. `nodeRows` keeps the rows of one group together, also for two networks of one name.
  const groups = new Map<string, { groupLabel: string; groupRows: typeof rows }>()
  for (const row of rows) {
    const group = groups.get(row.groupKey)
    if (group) group.groupRows.push(row)
    else groups.set(row.groupKey, { groupLabel: row.groupLabel, groupRows: [row] })
  }

  if (rows.length === 0) {
    return (
      <>
        <AddThisPhone onList />
        <NodesEmptyState />
      </>
    )
  }

  // Inside the noBGP app, this phone first: add it, approve it, or remove it.
  // ⚠ The tab is a column as high as the drawer, and each network's list shrinks to share it: the
  // lists fill the drawer and leave no space at the bottom. A large network shrinks more than a
  // small one, because a flex item shrinks in proportion to its height. Past the minimum of each
  // list, the drawer scrolls.
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <AddThisPhone onList />
      {[...groups].map(([groupKey, { groupLabel, groupRows }], index) => {
        const count = counts.get(groupKey) ?? outsideCounts.get(groupKey)
        return (
          <Fragment key={groupKey}>
            <ListSubheader component="div" sx={{ flexShrink: 0, marginX: -2, marginBottom: 1, marginTop: index > 0 ? 2 : 0 }} disableSticky>
              {groupLabel}{' '}
              {/* ⚠ Offline nodes can be hidden, and then the total is not known: see `countLabel`.
                  The space keeps the label and the count two words for a screen reader. */}
              <Typography
                component="span"
                variant="caption"
                color="textSecondary"
                sx={{ display: 'inline', marginLeft: 0.5, textTransform: 'none', letterSpacing: 'normal' }}
              >
                {countLabel(count, nodesComplete)}
              </Typography>
            </ListSubheader>
            {/* A large network scrolls in its own frame, and the networks below it stay in reach. */}
            <ScrollList minRows={MIN_ROWS} rowHeight={ROW_HEIGHT}>
              {groupRows.map(({ key, node, name, others }) => {
                const offline = isNodeOffline(node)
                return (
                  <ListItemLink key={key} to={nodePagePath(node.id)} icon={<IconNode type="node" offline={offline} size={20} />}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <Typography variant="body2">{name}</Typography>
                      <NodeVersionMark node={node} network={networks.find(n => n.id === node.networkIds[0])} />
                      <AlsoInMark others={others} />
                      {/* The marks after the name (nobgp-router#2010). The compute detail is on the node
                          page: Benoit, 2026-10-20, "no weird compute small icons in the badge". */}
                      <NodeMarks nodeId={node.id} compute={compute.get(node.id)} />
                    </Stack>
                    <Typography variant="caption" color="textSecondary" component="div">
                      {offlineLabel(offline, node.offlineAt, now)}
                    </Typography>
                  </ListItemLink>
                )
              })}
            </ScrollList>
          </Fragment>
        )
      })}
    </Box>
  )
}
